import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Sidebar from "../components/Sidebar.jsx";
import Navbar from "../components/Navbar.jsx";
import CreateTicketModal from "../components/CreateTicketModal.jsx";
import ToastNotification from "../components/ToastNotification.jsx";
import "../App.css";

const SECTIONS = [
  { id: "connexion",     icon: "fas fa-sign-in-alt",   label: "Se connecter" },
  { id: "tickets",       icon: "fas fa-list-alt",       label: "Consulter ses tickets" },
  { id: "creer",         icon: "fas fa-plus-circle",    label: "Créer un ticket" },
  { id: "relancer",      icon: "fas fa-sync-alt",       label: "Relancer un ticket" },
  { id: "cloture",       icon: "fas fa-lock",           label: "Demander la clôture" },
  { id: "details",       icon: "fas fa-info-circle",    label: "Voir les détails" },
  { id: "deconnexion",   icon: "fas fa-sign-out-alt",   label: "Se déconnecter" },
];

function HelpCard({ id, icon, title, children }) {
  return (
    <div className="card shadow mb-4" id={id} style={{ borderRadius: "12px", border: "none", overflow: "hidden" }}>
      <div className="card-header py-3 bg-white border-bottom-0">
        <h6 className="m-0 font-weight-bold" style={{ color: "var(--primary)" }}>
          <i className={`${icon} me-2`} aria-hidden="true"></i>
          {title}
        </h6>
      </div>
      <div className="card-body bg-light">
        {children}
      </div>
    </div>
  );
}

function Step({ number, text, detail }) {
  return (
    <div className="d-flex mb-3">
      <div
        className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
        style={{
          width: 32, height: 32,
          background: "var(--primary)",
          color: "#fff",
          fontWeight: "bold",
          fontSize: "0.9rem",
        }}
        aria-hidden="true"
      >
        {number}
      </div>
      <div>
        <div className="font-weight-bold">{text}</div>
        {detail && <div className="text-muted small mt-1">{detail}</div>}
      </div>
    </div>
  );
}

function Note({ children }) {
  return (
    <div
      className="p-3 mt-3"
      style={{
        backgroundColor: "#fff8e1",
        borderLeft: "4px solid var(--warning)",
        borderRadius: "4px",
        fontSize: "0.9rem",
      }}
    >
      <i className="fas fa-lightbulb me-2" style={{ color: "var(--warning)" }} aria-hidden="true"></i>
      {children}
    </div>
  );
}

export default function Help() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const { filtre, setFiltre } = useAuth();
  const navigate = useNavigate();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [toast, setToast] = useState({ visible: false, type: "success", message: "" });

  const handleFilterChange = (f) => {
    setFiltre(f);
    navigate('/tickets');
  };

  const scrollTo = (id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div id="wrapper" className="d-flex">
      <Helmet>
        <title>Aide — Niralakka Extranet</title>
      </Helmet>

      <Sidebar
        isOnTicketPage={true}
        filtre={filtre}
        onFilterChange={handleFilterChange}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        onCreateTicket={() => setCreateModalOpen(true)}
      />

      <div id="content-wrapper" className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
        <Navbar
          isOnTicketPage={false}
          searchTerm=""
          handleSearchChange={() => {}}
          showSearchResults={false}
          setShowSearchResults={() => {}}
          filteredTickets={[]}
          selectTicket={() => {}}
          typesTicket={{}}
          TICKET_STATUTS={{}}
          isSidebarCollapse={isCollapsed}
        />

        <div id="content" className="flex-grow-1">
          <div className="container-fluid custom-container">

            {/* En-tête */}
            <div className="d-sm-flex align-items-center justify-content-between mb-4">
              <h1 className="h3 mb-0 text-gray-800">
                <i className="fas fa-question-circle me-2" aria-hidden="true"></i>
                Aide &amp; Guide d'utilisation
              </h1>
            </div>

            <div className="row">
              {/* Sommaire (colonne gauche) */}
              <div className="col-lg-3 mb-4">
                <div className="card shadow" style={{ borderRadius: "12px", border: "none", position: "sticky", top: "1rem" }}>
                  <div className="card-header py-3 bg-white border-bottom-0">
                    <h6 className="m-0 font-weight-bold" style={{ color: "var(--primary)" }}>
                      <i className="fas fa-book-open me-2" aria-hidden="true"></i>
                      Sommaire
                    </h6>
                  </div>
                  <div className="card-body p-0">
                    <nav aria-label="Sommaire">
                      <ul className="list-unstyled mb-0">
                        {SECTIONS.map((s) => (
                          <li key={s.id}>
                            <button
                              className={`btn btn-link w-100 text-left px-3 py-2${activeSection === s.id ? " font-weight-bold" : " text-muted"}`}
                              style={{ color: activeSection === s.id ? "var(--primary)" : undefined, fontSize: "0.9rem" }}
                              onClick={() => scrollTo(s.id)}
                            >
                              <i className={`${s.icon} me-2`} aria-hidden="true"></i>
                              {s.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>

              {/* Contenu (colonne droite) */}
              <div className="col-lg-9">

                {/* 1. Se connecter */}
                <HelpCard id="connexion" icon="fas fa-sign-in-alt" title="Se connecter">
                  <p>L'extranet Niralakka ne nécessite pas de mot de passe spécifique. La connexion s'effectue via votre compte professionnel Microsoft ou Google.</p>
                  <Step number={1} text="Accédez à la page de connexion" detail="L'URL vous sera communiquée par votre prestataire Niralakka." />
                  <Step number={2} text="Cliquez sur « Connexion Microsoft » ou « Connexion Google »" detail="Utilisez le compte e-mail associé à votre entreprise." />
                  <Step number={3} text="Autorisez l'application si demandé" detail="Une fenêtre de votre fournisseur d'identité s'ouvrira. Cliquez sur « Autoriser »." />
                  <Step number={4} text="Vous êtes redirigé vers la liste de vos tickets" detail="La connexion est mémorisée pendant 8 heures." />
                  <Note>Si votre session expire, vous serez automatiquement redirigé vers la page de connexion avec un message d'avertissement.</Note>
                </HelpCard>

                {/* 2. Consulter ses tickets */}
                <HelpCard id="tickets" icon="fas fa-list-alt" title="Consulter ses tickets">
                  <p>La page principale affiche l'ensemble de vos tickets de support, classés par date de création.</p>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="card border-0 bg-white shadow-sm p-3 h-100">
                        <h6 className="font-weight-bold" style={{ color: "var(--primary)" }}>
                          <i className="fas fa-ticket-alt me-2" aria-hidden="true"></i>Tickets ouverts
                        </h6>
                        <p className="small text-muted mb-0">Tickets en cours de traitement ou en attente. Cliquez sur <strong>« Tickets ouverts »</strong> dans le menu latéral.</p>
                      </div>
                    </div>
                    <div className="col-md-6 mt-3 mt-md-0">
                      <div className="card border-0 bg-white shadow-sm p-3 h-100">
                        <h6 className="font-weight-bold text-muted">
                          <i className="fas fa-lock me-2" aria-hidden="true"></i>Tickets fermés
                        </h6>
                        <p className="small text-muted mb-0">Tickets clôturés. Cliquez sur <strong>« Tickets fermés »</strong> pour les afficher.</p>
                      </div>
                    </div>
                  </div>
                  <Step number={1} text="Utilisez la barre de recherche en haut" detail="Tapez un mot-clé (titre, statut, type) pour filtrer les tickets en temps réel." />
                  <Step number={2} text="Cliquez sur l'icône œil 👁 pour accéder aux détails d'un ticket" />
                  <Step number={3} text="Naviguez entre les pages avec le sélecteur de pagination en bas du tableau" />
                  <Note>Les tickets prioritaires sont signalés par une icône d'avertissement orange <i className="fas fa-exclamation-triangle text-warning" aria-hidden="true"></i>.</Note>
                </HelpCard>

                {/* 3. Créer un ticket */}
                <HelpCard id="creer" icon="fas fa-plus-circle" title="Créer un ticket">
                  <p>Vous pouvez soumettre une nouvelle demande à l'équipe Niralakka à tout moment.</p>
                  <Step number={1} text="Cliquez sur le bouton « Créer un ticket »" detail="Disponible en haut de la liste des tickets ouverts." />
                  <Step number={2} text="Saisissez un titre explicite" detail="Ex : « Problème de connexion au logiciel X »." />
                  <Step number={3} text="Renseignez votre numéro de téléphone à 10 chiffres" detail="Il sera intégré automatiquement au titre pour faciliter le rappel." />
                  <Step number={4} text="Choisissez la priorité" detail="Normal ou Urgent. Réservez le niveau « Urgent » aux blocages critiques." />
                  <Step number={5} text="Décrivez votre problème dans le champ « Description »" detail="Plus la description est précise, plus la résolution sera rapide." />
                  <Step number={6} text="Ajoutez des fichiers si nécessaire" detail="Glissez-déposez des captures d'écran ou documents dans la zone dédiée." />
                  <Step number={7} text="Cliquez sur « Valider »" detail="Un message de confirmation s'affiche et le ticket apparaît dans la liste." />
                  <Note>Le titre du ticket sera automatiquement formaté : <strong>Votre nom — 06 XX XX XX XX — Titre saisi</strong>.</Note>
                </HelpCard>

                {/* 4. Relancer un ticket */}
                <HelpCard id="relancer" icon="fas fa-sync-alt" title="Relancer un ticket">
                  <p>Si vous n'avez pas reçu de retour sous un délai raisonnable, vous pouvez relancer votre ticket.</p>
                  <Step number={1} text="Repérez le bouton de relance (icône ↻) sur la ligne du ticket" detail="Disponible uniquement pour les tickets en statut Ouvert ou En cours." />
                  <Step number={2} text="Cliquez dessus pour ouvrir la modale de relance" />
                  <Step number={3} text="Saisissez un message précisant votre besoin" detail="Ex : « Toujours bloqué, intervention urgente nécessaire. »" />
                  <Step number={4} text="Validez en cliquant sur « Valider »" detail="La relance est enregistrée dans l'historique du ticket." />
                  <Note>Le bouton est désactivé si une demande de clôture est déjà en attente ou si vous avez relancé récemment.</Note>
                </HelpCard>

                {/* 5. Demander la clôture */}
                <HelpCard id="cloture" icon="fas fa-lock" title="Demander la clôture d'un ticket">
                  <p>Lorsque votre problème est résolu, vous pouvez demander la clôture du ticket.</p>
                  <Step number={1} text="Sur la ligne du ticket, cliquez sur l'icône 🔒 (clôture)" detail="Ou sur le bouton « Demander à clôturer » dans la page de détails." />
                  <Step number={2} text="Rédigez un message de confirmation" detail="Ex : « Le problème est résolu, merci. »" />
                  <Step number={3} text="Validez en cliquant sur « Valider »" detail="Une notification de succès confirme l'envoi de la demande." />
                  <Note>La clôture définitive est effectuée par l'équipe Niralakka. Le ticket passera en statut « Clôturé » une fois validé.</Note>
                </HelpCard>

                {/* 6. Voir les détails */}
                <HelpCard id="details" icon="fas fa-info-circle" title="Voir les détails d'un ticket">
                  <p>La page de détails regroupe toutes les informations d'un ticket : description, statut, intervenants, historique et fichiers joints.</p>
                  <Step number={1} text="Cliquez sur l'icône 👁 sur la ligne du ticket dans la liste" />
                  <Step number={2} text="Consultez la section « Détails du ticket »" detail="Date de création, statut, intervenants assignés, description complète." />
                  <Step number={3} text="Parcourez l'historique des interventions" detail="Toutes les actions (relances, commentaires, mises à jour) sont listées chronologiquement." />
                  <Step number={4} text="Consultez les fichiers joints" detail="Cliquez sur un fichier pour l'afficher ou le télécharger." />
                  <Step number={5} text="Ajoutez des fichiers complémentaires" detail="Glissez-déposez dans la zone d'upload, puis cliquez sur « Ajouter »." />
                  <Note>Le bouton « Modifier » (crayon) n'est disponible que pour les tickets ouverts ou en cours dont vous êtes le demandeur.</Note>
                </HelpCard>

                {/* 7. Se déconnecter */}
                <HelpCard id="deconnexion" icon="fas fa-sign-out-alt" title="Se déconnecter">
                  <Step number={1} text="Cliquez sur votre nom en haut à droite de la barre de navigation" />
                  <Step number={2} text="Cliquez sur « Se déconnecter »" detail="Votre session est clôturée et vous êtes redirigé vers la page de connexion." />
                  <Note>Votre session expire automatiquement après 8 heures d'inactivité pour des raisons de sécurité.</Note>
                </HelpCard>

              </div>
            </div>
          </div>
        </div>

        <footer className="sticky-footer bg-white">
          <div className="container my-auto">
            <div className="copyright text-center my-auto text-muted small">
              Niralakka Extranet — <a href="mailto:support@niralakka.fr">support@niralakka.fr</a>
            </div>
          </div>
        </footer>
      </div>

      {createModalOpen && (
        <CreateTicketModal
          onClose={() => setCreateModalOpen(false)}
          onSubmitSuccess={() => {
            setCreateModalOpen(false);
            setToast({ visible: true, type: "success", message: "Ticket créé avec succès ✅" });
          }}
          onError={() => setToast({ visible: true, type: "error", message: "Échec de la création ❌" })}
        />
      )}

      {toast.visible && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ visible: false })}
        />
      )}
    </div>
  );
}
